import React from 'react'
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Stack
} from '@chakra-ui/react'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { capabilities } from '@homevest/utils'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom'

import { hasCapability } from 'lib/admin-perms'
import { StoreState } from 'store'
import PayOrQuitPostingPanel from './PayOrQuitPostingPanel'
import {
  collectionsMonths,
  collectionsYears,
  payOrQuitPostingHref,
  payOrQuitPostingSelection,
  payOrQuitPostingStates,
  PayOrQuitPostingSelection
} from './payOrQuitPostingNavigation'

const PayOrQuitPostingPage = () => {
  const location = useLocation()
  const history = useHistory()
  const selection = payOrQuitPostingSelection(location.search)
  const admin = useSelector((state: StoreState) => state.admin)
  const canGenerateNotices = hasCapability(admin, capabilities.CAPABILITY_TYPES.PAY_OR_QUIT_SENDER)
  const selectPosting = (change: Partial<PayOrQuitPostingSelection>) => {
    history.replace(payOrQuitPostingHref({ ...selection, ...change }))
  }

  return (
    <>
      <Helmet>
        <title>Generate notices for posting</title>
      </Helmet>
      <Stack width='full' maxWidth='6xl' marginX='auto' padding={4} spacing={4}>
        <Button
          as={RouterLink}
          to={`/collections?month=${selection.month}&year=${selection.year}`}
          alignSelf='flex-start'
          leftIcon={<ArrowLeftIcon className='h-4 w-4' />}
          variant='ghost'
          size='sm'
        >
          Back to Collections
        </Button>
        <Heading>Generate notices for posting</Heading>
        {canGenerateNotices ? (
          <>
            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              <FormControl>
                <FormLabel htmlFor='posting-state'>State</FormLabel>
                <Select
                  id='posting-state'
                  value={selection.state}
                  onChange={(event) => selectPosting({ state: event.currentTarget.value })}
                >
                  {payOrQuitPostingStates.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel htmlFor='posting-month'>Collections month</FormLabel>
                <Select
                  id='posting-month'
                  value={selection.month}
                  onChange={(event) => selectPosting({ month: Number(event.currentTarget.value) })}
                >
                  {collectionsMonths.map((month, index) => (
                    <option key={month} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel htmlFor='posting-year'>Year</FormLabel>
                <Select
                  id='posting-year'
                  value={selection.year}
                  onChange={(event) => selectPosting({ year: Number(event.currentTarget.value) })}
                >
                  {collectionsYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <PayOrQuitPostingPanel
              key={`${selection.state}-${selection.year}-${selection.month}`}
              state={selection.state}
              month={selection.month}
              year={selection.year}
            />
          </>
        ) : (
          <Alert status='warning'>
            <AlertIcon />
            You need Pay or Quit access to generate notices for posting.
          </Alert>
        )}
      </Stack>
    </>
  )
}

export default PayOrQuitPostingPage
